import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  DropdownGroup,
  DropdownItem,
  NotificationDrawerListItem,
  NotificationDrawerListItemBody,
  NotificationDrawerListItemHeader,
  PageSection,
  PageSidebar,
  PageSidebarBody,
} from "@patternfly/react-core";
import { Link, Outlet, useNavigate } from "react-router-dom";

import ApplicationLayout from "./app/components/layout/ApplicationLayout";
import Brand from "./app/components/layout/Brand";
import useRole from "./app/shared/hooks/user/useRole";
import useFullName from "./app/shared/hooks/user/useFullName";
import { useGetNotificationsQuery } from "./features/api/getNotificationsApi";
import { useMarkNotificationAsReadMutation } from "./features/api/markNotificationAsRead";
import ApplicationSidebarNav from "./app/components/layout/ApplicationSidebarNav";
import useDispatchMarkNotificationAsReadToast from "./app/components/toasts/hooks/useDispatchMarkNotificationAsReadToast";
import useEmail from "./app/shared/hooks/user/useEmail";
import { useUnleashContext } from "@unleash/proxy-client-react";
import useColorSchemePreference from "./app/shared/hooks/useColorSchemePreference";

const brand = <Brand widths={{ default: "180px" }} />;
const sidebar = (
  <PageSidebar>
    <PageSidebarBody>
      <ApplicationSidebarNav />
    </PageSidebarBody>
  </PageSidebar>
);

function App() {
  useColorSchemePreference();
  const role = useRole();
  const name = useFullName();
  const email = useEmail();
  const updateContext = useUnleashContext();
  useEffect(() => {
    void updateContext({
      userId: email ?? undefined,
    });
  }, [updateContext, email]);
  const navigate = useNavigate();
  const [isNotificationDrawerExpanded, setIsNotificationDrawerExpanded] =
    useState(false);

  const toggleNotificationDrawer = useCallback(() => {
    setIsNotificationDrawerExpanded((prev) => !prev);
  }, [setIsNotificationDrawerExpanded]);
  const handleNotificationBellClick = toggleNotificationDrawer;
  const handleNotificationDrawerClose = toggleNotificationDrawer;
  const { data: notifications } = useGetNotificationsQuery(undefined);
  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const { dispatchFailureToast: dispatchMarkNotificationAsReadFailureToast } =
    useDispatchMarkNotificationAsReadToast();

  const dropdownItems = useDropdownItems();

  const notificationListItems = useMemo(() => {
    return (
      notifications?.map((notification) => {
        const handleNotificationClick = () => {
          markNotificationAsRead(notification.id)
            .unwrap()
            .then((read) => {
              if (read) {
                const url = `${import.meta.env.BASE_URL}/external/dashboard/${
                  notification.eligibility
                }`;
                navigate(url)?.catch(() => {
                  console.error("Failed to navigate to dashboard");
                });
                setIsNotificationDrawerExpanded((prev) => !prev);
              }
            })
            .catch(() => {
              dispatchMarkNotificationAsReadFailureToast();
            });
        };
        return (
          <NotificationDrawerListItem
            key={notification.id}
            onClick={handleNotificationClick}
            isRead={notification.read}
          >
            <NotificationDrawerListItemHeader
              title={notification.eligibility}
            ></NotificationDrawerListItemHeader>
            <NotificationDrawerListItemBody>
              {notification.message}
            </NotificationDrawerListItemBody>
          </NotificationDrawerListItem>
        );
      }) ?? []
    );
  }, [
    notifications,
    markNotificationAsRead,
    navigate,
    setIsNotificationDrawerExpanded,
    dispatchMarkNotificationAsReadFailureToast,
  ]);

  return (
    <ApplicationLayout
      notifications={notificationListItems}
      onNotificationBellClick={handleNotificationBellClick}
      onNotificationDrawerClose={handleNotificationDrawerClose}
      isNotificationDrawerExpanded={isNotificationDrawerExpanded}
      hideHeaderToolbar={role === null}
      name={name ?? undefined}
      userDropdownItems={dropdownItems}
      brand={brand}
      sidebar={sidebar}
    >
      <PageSection hasBodyWrapper={false}>
        <Outlet />
      </PageSection>
    </ApplicationLayout>
  );
}

export default App;

function useDropdownItems(): React.JSX.Element[] {
  return [
    <DropdownGroup key="group">
      <DropdownItem
        component={(props) => <Link {...props} to="settings/profile" />}
      >
        My Profile
      </DropdownItem>
      <DropdownItem component={(props) => <Link {...props} to="log-out" />}>
        Logout
      </DropdownItem>
    </DropdownGroup>,
  ];
}
